/**
 * Mappers between Core::Catalog API responses (backend) and the legacy
 * yeati-loc frontend types (UI source of truth).
 *
 * Why an adapter?
 * - The frontend was originally built against in-memory mocks with a different
 *   shape (`RentalProduct.pricing.periods[]`, `category` as a fixed taxonomy,
 *   prices in euros, status `draft|published|archived`).
 * - The backend uses normalized polymorphic resources (PricingRule),
 *   user-defined Category trees, prices in cents, AASM booking states.
 *
 * This adapter keeps the existing UI working while the integration is
 * progressively reshaped against the real API.
 */
import { DEFAULT_CATEGORIES } from "../configs/constants";
import { hasBookingRules, normalizeBookingRules, toApiBookingRules } from "../utils/bookingRules";
import { adaptVoyageGridResponse, adaptVoyageLines, isVoyageCategory } from "../utils/voyageGrid";
import type {
  CatalogBookingResponse,
  CatalogBookingStatus,
  CatalogFormFieldType,
  CatalogItemCreatePayload,
  CatalogItemMode,
  CatalogItemResponse,
  CatalogItemUpdatePayload,
  CatalogPriceType,
  CatalogPricingRuleResponse,
  CatalogStockStrategy,
  CatalogVariantResponse,
} from "../types/catalog.api.types";
import type {
  BookingFormResponseSubField,
  BookingStatus,
  CreateProductPayload,
  PricingType,
  ProductStatus,
  RentalBooking,
  RentalCategory,
  RentalFormField,
  RepeatEntry,
  RentalPeriod,
  RentalPricing,
  RentalProduct,
  RentalSlot,
  RentalVariant,
  RentalVariantRule,
  UpdateProductPayload,
} from "../types/yeatiLoc.types";

const FALLBACK_CATEGORY: RentalCategory =
  DEFAULT_CATEGORIES.find((c) => c.id === "cat_materiel") ??
  DEFAULT_CATEGORIES[0];

const findCategory = (id: string): RentalCategory =>
  DEFAULT_CATEGORIES.find((c) => c.id === id) ?? FALLBACK_CATEGORY;

// Keyword → UI category. Order matters: the first match wins, so put the most
// specific buckets first (e.g. "salle" before "matériel").
const CATEGORY_KEYWORD_RULES: Array<{ id: string, patterns: RegExp }> = [
  // Hebergement: lodging-style items
  { id: "cat_hebergement", patterns: /\b(appartement|gîte|gite|mobil[\s-]?home|maison|villa|chalet|studio|chambre|h[éeè]bergement|logement|location\s+vacance)/i },
  // Salle: event spaces
  { id: "cat_salle", patterns: /\b(salle|r[éeè]union|f[êeè]te|conf[éeè]rence|s[éeè]minaire|[éeè]v[éeè]nement)/i },
  // Vehicule
  { id: "cat_vehicule", patterns: /\b(voiture|v[éeè]hicule|utilitaire|minibus|v[éeè]lo|trottinette|auto|camion|moto|scooter)/i },
  // Emplacement: campsites, plots
  { id: "cat_emplacement", patterns: /\b(emplacement|terrain|parcelle|camping)/i },
];

/**
 * UI-category resolution.
 *
 * The backend persists the high-level taxonomy bucket on `catalog_items.kind`
 * (values: hebergement, materiel, emplacement, vehicule, salle). When set,
 * we map it to the corresponding `cat_*` UI category. For legacy rows where
 * `kind` is null, we fall back to keyword heuristics on name + description.
 */
function inferUiCategory(item: CatalogItemResponse): RentalCategory {
  if (item.kind) {
    const direct = DEFAULT_CATEGORIES.find((c) => c.id === `cat_${item.kind}`);
    if (direct) return direct;
  }
  const haystack = `${item.name} ${item.description ?? ""}`.toLowerCase();
  for (const rule of CATEGORY_KEYWORD_RULES) {
    if (rule.patterns.test(haystack)) return findCategory(rule.id);
  }
  return FALLBACK_CATEGORY;
}

/** Map a UI category id (e.g. "cat_hebergement") to the backend `kind` value. */
function categoryIdToKind(categoryId: string | undefined): string | undefined {
  if (!categoryId) return undefined;
  const stripped = categoryId.startsWith("cat_") ? categoryId.slice(4) : categoryId;
  return DEFAULT_CATEGORIES.some((c) => c.id === `cat_${stripped}`) ? stripped : undefined;
}

// ---------------------------------------------------------------------------
// Backend → Frontend
// ---------------------------------------------------------------------------

const apiPriceTypeToUi: Record<CatalogPriceType, PricingType> = {
  flat: "fixed",
  per_day: "per_day",
  per_night: "per_night",
  per_week: "per_week",
  // The backend supports per_unit but the UI doesn't; map to "fixed" as a
  // close-enough approximation for display.
  per_unit: "fixed",
  period_total: "period_total",
  duration_tier: "duration_tier",
};

const uiPricingTypeToApi: Record<PricingType, CatalogPriceType> = {
  fixed: "flat",
  per_day: "per_day",
  per_night: "per_night",
  per_week: "per_week",
  period_total: "period_total",
  duration_tier: "duration_tier",
};

export function apiItemStatusToUi(item: CatalogItemResponse): ProductStatus {
  // Le backend expose désormais `status` en tri-state autoritaire. On
  // garde un fallback sur `active` pour les payloads historiques (et
  // ceux des contextes booking où le item embed est minimal).
  if (item.status === "draft" || item.status === "published" || item.status === "archived") {
    return item.status;
  }
  return item.active ? "published" : "archived";
}

function pickPrimaryRule(
  rules: CatalogPricingRuleResponse[],
): CatalogPricingRuleResponse | null {
  if (!rules.length) return null;
  return [...rules].sort((a, b) => b.priority - a.priority)[0];
}

// Hébergement : les variantes sont des tarifs du même logement (Tarif A /
// B / C). Elles partagent le calendrier de l'article côté API et portent
// un prix par saison de l'article, rattaché par ses bornes de dates.
const isTarifCategory = (categoryId: string | undefined): boolean =>
  categoryId === "cat_hebergement";

// Règles propres d'une variante, telles que l'API les applique : la plus
// prioritaire d'abord. Vide quand la variante suit les règles de l'article.
function variantOwnRules(variant: CatalogVariantResponse): RentalVariantRule[] {
  return [...(variant.pricing_rules ?? [])]
    .sort((a, b) => b.priority - a.priority)
    .map((rule) => ({
      price_type: apiPriceTypeToUi[rule.price_type] ?? "fixed",
      start_date: rule.valid_from ?? "",
      end_date: rule.valid_to ?? "",
    }));
}

function adaptTarifVariant(
  variant: CatalogVariantResponse,
  periods: RentalPeriod[],
  isFirstColumn: boolean,
): RentalVariant {
  const rules = variant.pricing_rules ?? [];
  const used = new Set<string>();
  const seasonPrices = periods.map((period) => {
    const rule = rules.find((r) => !used.has(r.id)
      && (r.valid_from ?? "") === period.start_date
      && (r.valid_to ?? "") === period.end_date);
    if (rule) used.add(rule.id);
    // Première colonne : un prix « vide » est enregistré au prix de base
    // de la saison (le tarif doit couvrir toute l'année). Le relire comme
    // vide garde la promesse de l'écran : il suit ensuite le prix de base
    // s'il change. Les autres tarifs ont un prix obligatoire : égal au prix
    // de base, il reste leur prix.
    const followsBase = isFirstColumn && rule?.amount_cents === Math.round((period.price_per_unit ?? 0) * 100);
    return {
      period_id: period.id,
      price: rule && !followsBase ? rule.amount_cents / 100 : undefined,
      rule_id: rule?.id,
    };
  });
  return {
    id: variant.id,
    name: variant.name,
    description: variant.description,
    active: variant.active,
    sku: variant.sku,
    stock_quantity: null,
    season_prices: seasonPrices,
    stale_rule_ids: rules.filter((r) => !used.has(r.id)).map((r) => r.id),
    own_rules: variantOwnRules(variant),
  };
}

export function adaptItemToProduct(item: CatalogItemResponse): RentalProduct {
  const primaryRule = pickPrimaryRule(item.pricing_rules);
  const category = inferUiCategory(item);

  // `seasonal` n'est pas une colonne backend - c'est un drapeau UI qu'on
  // dérive de la forme des rules : dès qu'on a >1 règle, OU qu'une
  // règle porte des bornes valid_from/valid_to, on est en mode
  // saisonnier (le PricingEditor doit rendre les period cards, pas le
  // FixedPriceCard). Sans cette dérivation, un item matériel avec
  // tarifs saisonniers (cf. Tente basse/haute saison/automne) ne
  // rendait que la 1ʳᵉ période visible côté admin. Les rules variant
  // sont nested sous `item.variants[].pricing_rules`, donc
  // `item.pricing_rules` ne contient déjà que les rules item-level.
  const seasonal = item.pricing_rules.length > 1
    || item.pricing_rules.some((rule) => rule.valid_from || rule.valid_to);

  const pricing: RentalPricing = {
    type: primaryRule ? apiPriceTypeToUi[primaryRule.price_type] : "fixed",
    periods: item.pricing_rules.map((rule) => ({
      id: rule.id,
      name:
        rule.valid_from && rule.valid_to
          ? `${rule.valid_from} → ${rule.valid_to}`
          : "Tarif standard",
      start_date: rule.valid_from ?? "",
      end_date: rule.valid_to ?? "",
      price_per_unit: rule.amount_cents / 100,
      ...(rule.price_type === "duration_tier" ? { min_days: rule.min_days ?? null, max_days: rule.max_days ?? null } : {}),
    })),
    seasonal,
  };

  return {
    id: item.id,
    title: item.name,
    demo: item.demo ?? false,
    description: item.description ?? "",
    category,
    category_ids: item.category_ids ?? [],
    complement_item_ids: item.complement_item_ids ?? [],
    // Filtre les pièces sans `signed_id` (cas pathologique : un legacy
    // serializer pourrait omettre le champ) - l'UI a besoin du signedId
    // pour préserver l'image au prochain submit (sinon une simple
    // sauvegarde « sans modif photos » la perdrait).
    images: (item.images ?? []).flatMap((img) => (
      img.signed_id ? [{ signed_id: img.signed_id, url: img.url }] : []
    )),
    status: apiItemStatusToUi(item),
    pricing,
    availability: {
      start_days:
        item.check_in_day !== null && item.check_in_day !== undefined
          ? [item.check_in_day]
          : [],
      min_duration: item.min_duration_days ?? 1,
      allow_extra_nights: false,
      extra_nights_step: 1,
    },
    duration_step_days: item.duration_step_days ?? null,
    settings: {
      // Champs catégorie-spécifiques (jsonb backend) - condition, brand,
      // amenities, capacity, … Le spread est en premier pour que nos
      // clés UI standardisées (mode/stock/etc.) ne soient pas écrasées
      // par un éventuel doublon côté backend.
      ...(item.settings ?? {}),
      mode: item.mode,
      stock_strategy: item.stock_strategy,
      stock_quantity: item.stock_quantity,
      purchase_workflow: item.purchase_workflow,
      check_in_day: item.check_in_day,
      check_out_day: item.check_out_day,
      max_duration_days: item.max_duration_days,
      collection_prefix_id: item.collection_prefix_id,
      // Colonnes dédiées côté API (hors jsonb `settings`) — on les
      // remonte dans `settings` pour que l'édition d'un item pré-remplisse
      // les panels « Notifications élus » et « Méthodes de remise »
      // (sinon le champ apparaît vide alors que la valeur est en base).
      delivery_methods: item.delivery_methods ?? [],
      notification_recipient_ids: item.notification_recipient_ids ?? [],
      // Rangées côté API dans le jsonb `settings`, mais servies à part.
      booking_rules: normalizeBookingRules(item.booking_rules ?? item.settings?.booking_rules),
    },
    form_fields: item.form_fields?.map((f) => ({
      id: f.id,
      name: f.name,
      label: f.label,
      field_type: f.field_type,
      required: f.required,
      rank: f.rank,
      options: f.options ?? {},
    })) ?? [],
    slots: item.slots?.map((s) => ({
      id: s.id,
      name: s.name,
      start_date: s.start_date,
      end_date: s.end_date,
      capacity: s.capacity,
      remaining_capacity: s.remaining_capacity,
      active: s.active,
    })) ?? [],
    // Variantes (billetterie : VIP/Fosse) - on garde un mapping 1 prix
    // par variant : la première règle prend l'amount, les éventuelles
    // règles supplémentaires (saisonnalité par variant) ne sont pas
    // exposées dans le builder simple actuel. Le backend cascade vers
    // les pricing_rules de l'item parent si le variant n'a aucune règle.
    variants: item.variants?.map((v, index) => {
      if (isTarifCategory(category.id)) return adaptTarifVariant(v, pricing.periods, index === 0);
      const firstRule = v.pricing_rules?.[0];
      return {
        id: v.id,
        name: v.name,
        description: v.description,
        active: v.active,
        sku: v.sku,
        stock_quantity: v.stock_quantity,
        price: firstRule ? firstRule.amount_cents / 100 : undefined,
        price_type: firstRule
          ? (apiPriceTypeToUi[firstRule.price_type] ?? "fixed")
          : undefined,
        pricing_rule_id: firstRule?.id,
        own_rules: variantOwnRules(v),
      };
    }) ?? [],
    deposit_amount: (item.deposit_amount_cents ?? 0) / 100,
    cb_payment_enabled: item.cb_payment_enabled,
    collection_cb_payment_enabled: item.collection_cb_payment_enabled ?? false,
    stock_management_enabled: item.stock_management_enabled ?? null,
    collection_stock_management_enabled: item.collection_stock_management_enabled ?? true,
    stock_management_allowed:
      item.stock_management_enabled ?? item.collection_stock_management_enabled ?? true,
    cart_blocks_slot: item.cart_blocks_slot ?? null,
    collection_cart_blocks_slot: item.collection_cart_blocks_slot ?? false,
    // Indispos — présentes seulement sur le détail (show) côté API.
    unavailable_periods: item.unavailable_periods,
    voyage_grid: item.voyage_grid ? adaptVoyageGridResponse(item.voyage_grid) : null,
    created_at: item.created_at,
    updated_at: item.updated_at,
  };
}

export const apiBookingStatusToUi: Record<CatalogBookingStatus, BookingStatus> = {
  // Au panier : celui du salarié, ou le brouillon de l'élu.
  open: "cart",
  // Paiement en cours : pré-engagement, mais plus éditable (le montant
  // est en vol chez Paybox) - surtout pas « cart ».
  pending_checkout: "pending_checkout",
  cart: "cart",
  pending_approval: "pending",
  // Statuts unifiés 31/08 : « approved » est habitable - validée par
  // l'élu, payable par le bénéficiaire.
  approved: "approved",
  rejected: "rejected",
  confirmed: "confirmed",
  in_progress: "in_progress",
  return_pending: "pending_return",
  return_completed: "completed",
  // Anciens noms, payloads historiques.
  pending_return: "pending_return",
  completed: "completed",
  cancelled: "cancelled",
  ristourne: "ristourne",
};

export function adaptBookingResponse(
  booking: CatalogBookingResponse,
  // The backend exposes references via prefix IDs; the UI used to embed the
  // full RentalProduct object. We accept an optional resolver so callers can
  // provide a hydrated product from a separate fetch when needed.
  product?: RentalProduct,
): RentalBooking {
  // Prefer the explicit hydrated product, fall back to the inline summary
  // served by the admin booking endpoint, fall back to a placeholder if both
  // are missing (older payloads or fixture data).
  const inlineCategory = booking.item?.kind
    ? findCategory(`cat_${booking.item.kind}`)
    : FALLBACK_CATEGORY;

  const employee = booking.employee;
  const employeeName = employee?.fullname?.trim()
    || [employee?.first_name, employee?.last_name].filter(Boolean).join(" ").trim()
    || booking.employee_prefix_id
    || "Salarié";

  return {
    id: booking.id,
    reference: booking.reference,
    variant_name: booking.variant_name ?? null,
    product:
      product ??
      ({
        id: booking.item?.id ?? booking.item_prefix_id,
        title: booking.item?.name ?? "Item",
        description: "",
        category: inlineCategory,
        images: (booking.item?.images ?? []).flatMap((img) => (
          img.signed_id ? [{ signed_id: img.signed_id, url: img.url }] : []
        )),
        status: "published",
        pricing: { type: "fixed", periods: [] },
        availability: {
          start_days: [],
          min_duration: 1,
          allow_extra_nights: false,
          extra_nights_step: 1,
        },
        settings: {
          mode: booking.item?.mode,
          purchase_workflow: booking.item?.purchase_workflow,
        },
        deposit_amount: 0,
        cb_payment_enabled: null,
        collection_cb_payment_enabled: false,
        stock_management_enabled: null,
        collection_stock_management_enabled: true,
        cart_blocks_slot: null,
        collection_cart_blocks_slot: false,
        // Valeur effective servie par le payload booking admin - pilote
        // l'affichage des actions manuelles dans l'onglet Stock.
        stock_management_allowed: booking.item?.stock_management_allowed ?? true,
        created_at: booking.created_at,
        updated_at: booking.updated_at,
      } satisfies RentalProduct),
    purchase_workflow: booking.item?.purchase_workflow ?? "direct",
    beneficiary: {
      id: employee?.id ?? booking.employee_prefix_id ?? "",
      name: employeeName,
      email: employee?.email ?? "",
      demo_account: employee?.demo_account ?? false,
    },
    start_date: booking.start_date ?? "",
    end_date: booking.end_date ?? "",
    status: apiBookingStatusToUi[booking.status] ?? "cart",
    settlement_status: booking.settlement_status,
    total_price: booking.total_price_cents
      ? booking.total_price_cents / 100
      : 0,
    can_refund: booking.can_refund,
    can_cancel: booking.can_cancel,
    cancel_blocked_reason: booking.cancel_blocked_reason ?? null,
    requested_at: booking.requested_at,
    validated_at: booking.validated_at,
    validation_actor: booking.validation_actor,
    subsidy_reference_date: booking.subsidy_reference_date,
    subsidy_locked: booking.subsidy_locked
      ?? (Boolean(booking.subsidy_finalized_at) || !["open", "pending_approval"].includes(booking.status)),
    subsidy_amount: booking.subsidy_amount_cents
      ? booking.subsidy_amount_cents / 100
      : 0,
    deposit_amount: booking.deposit_amount_cents
      ? booking.deposit_amount_cents / 100
      : 0,
    payment_status: booking.payment_status,
    paid_at: booking.paid_at ?? null,
    payment_failure_reason: booking.payment_failure_reason ?? null,
    remaining_amount: booking.remaining_amount_cents !== null
      && booking.remaining_amount_cents !== undefined
      ? booking.remaining_amount_cents / 100
      : undefined,
    quantity: booking.quantity,
    refund_return_pending: booking.refund_return_pending,
    stock_delivered_quantity: booking.stock_delivered_quantity,
    stock_returned_quantity: booking.stock_returned_quantity,
    psp_transactions: booking.psp_transactions?.map((tx) => ({
      id: tx.id,
      operation_type: tx.operation_type,
      amount_cents: tx.amount_cents,
      code: tx.code,
      numtrans: tx.numtrans,
      created_at: tx.created_at,
    })),
    price_tier: booking.price_tier ?? null,
    voyage_lines: adaptVoyageLines(booking.voyage_lines),
    subsidy_breakdown: booking.subsidy_breakdown?.map((entry) => ({
      holder_type: entry.holder_type,
      holder_id: entry.holder_id,
      amount: Number(entry.amount) || 0,
      finalized: entry.finalized,
    })),
    activite_operations: booking.activite_operations?.map((ao) => ({
      id: ao.id,
      libelle: ao.libelle,
      categorie: ao.categorie,
      etat: ao.etat,
      attente: ao.attente,
      verrouille: ao.verrouille,
      facture_annulee: ao.facture_annulee,
      debit: Number(ao.debit ?? 0),
      credit: Number(ao.credit ?? 0),
      credit_before_subsidy: ao.credit_before_subsidy !== null && ao.credit_before_subsidy !== undefined
        ? Number(ao.credit_before_subsidy)
        : null,
      solde_affectation: Number(ao.solde_affectation ?? 0),
      date: ao.date,
      affectations: (ao.compta_mouvement_affectations ?? []).map((cma) => ({
        id: cma.id,
        libelle: cma.libelle,
        debit: Number(cma.debit ?? 0),
        credit: Number(cma.credit ?? 0),
        type_budget: cma.type_budget,
        type_mouvement: cma.type_mouvement,
        autre: cma.autre,
        date: cma.date,
        attente: cma.attente,
        bank_movement_id: cma.compta_mouvement_id,
      })),
    })),
    form_responses: booking.form_responses?.map((r) => {
      // The API returns the jsonb hash; we always wrote `{ value: ... }`
      // when collecting, so unwrap when present, otherwise expose the
      // raw payload as a JSON string for visibility.
      // Un groupe répété porte un tableau d'entrées.
      const { value: wrapped, sub_fields: snapshot, people } = r.value as {
        value?: string | number | boolean | RepeatEntry[]
        sub_fields?: unknown
        people?: unknown
      };
      const fallback = typeof r.value === "object" && r.value
        ? JSON.stringify(r.value)
        : null;
      return {
        id: r.id,
        field_label: r.field_label,
        field_type: r.field_type,
        value: wrapped ?? fallback,
        file: r.file ?? null,
        field_options: r.field_options ?? {},
        ...(Array.isArray(snapshot) ? { sub_fields: snapshot as BookingFormResponseSubField[] } : {}),
        ...(people === true ? { people: true } : {}),
      };
    }),
    return_record: booking.return_record
      ? {
        id: booking.return_record.id,
        condition: booking.return_record.condition,
        actual_date: booking.return_record.actual_date,
        penalty_amount_cents: booking.return_record.penalty_amount_cents,
        notes: booking.return_record.notes,
      }
      : null,
    notes: booking.notes ?? undefined,
    created_at: booking.created_at,
    updated_at: booking.updated_at,
  };
}

// ---------------------------------------------------------------------------
// Frontend → Backend (payload mappers)
// ---------------------------------------------------------------------------

type ProductCreateContext = {
  /**
   * The collection prefix_id the new item must belong to (required by the
   * backend). The UI wizard does not yet ask for it; callers must inject it.
   */
  catalogCollectionId: string
};

function inferModeFromUi(
  payload: CreateProductPayload | UpdateProductPayload,
): CatalogItemMode {
  // Même taxonomie que la voie de mise à jour : le kind décide. Les deux
  // divergeaient - la création rendait "rental" par défaut là où la mise à
  // jour mappait materiel/livre vers "loan", et un même article changeait
  // de mode selon la voie empruntée (ANO-21).
  const kind = categoryIdToKind(payload.category_id);
  if (kind === "hebergement") return "rental";
  if (kind === "materiel" || kind === "livre") return "loan";
  if (kind === "voyage") return "sale";

  const settings = payload.settings;
  const fromSettings = settings?.mode;
  if (fromSettings === "rental" || fromSettings === "sale" || fromSettings === "loan") {
    return fromSettings;
  }
  // Default: a CSE rental product is a "rental".
  return "rental";
}

function inferStockStrategy(
  payload: CreateProductPayload | UpdateProductPayload,
): CatalogStockStrategy {
  // Hébergement : le backend exige `calendar` (cf. `hebergement_constraints`
  // côté Item) puisque la dispo est portée par le calendrier et le pricing
  // par les séjours forfaitaires. On force ici pour ne pas que l'UI envoie
  // une valeur stale héritée d'une autre catégorie.
  if (isHebergement(payload)) return "calendar";
  // Voyage : les places sont portées par les départs (créneaux avec
  // capacité), contrôlées en quantité ; pas de calendrier d'article.
  if (isVoyage(payload)) return "quantity";

  const settings = payload.settings;
  const fromSettings = settings?.stock_strategy;
  if (fromSettings === "quantity" || fromSettings === "calendar") {
    return fromSettings;
  }
  return "calendar";
}

function isVoyage(payload: CreateProductPayload | UpdateProductPayload): boolean {
  return isVoyageCategory(payload.category_id);
}

function isHebergement(payload: CreateProductPayload | UpdateProductPayload): boolean {
  const categoryId = (payload as CreateProductPayload).category_id
    ?? (payload as UpdateProductPayload).category_id;
  return categoryId === "cat_hebergement";
}

// Clés de `settings` (UI) mappées sur des colonnes API dédiées (top-level
// ou nested_attributes). On les retire du jsonb `settings` pour ne pas y
// stocker de doublons obsolètes ; tout le reste (condition, brand,
// amenities, capacity, bedrooms, surface, location, pets_allowed, …) part
// dans le jsonb tel quel pour persistance des champs catégorie-spécifiques.
const SETTINGS_COLUMN_KEYS = new Set([
  "mode", "stock_strategy", "stock_quantity", "purchase_workflow",
  "check_in_day", "check_out_day", "min_duration_days", "max_duration_days",
  "duration_step_days",
  "deposit_amount_cents", "cb_payment_enabled", "stock_management_enabled",
  "cart_blocks_slot",
  "collection_prefix_id",
  "delivery_methods", "notification_recipient_ids",
  // Écrites par la clé `booking_rules` : l'API ignore celles d'un `settings`.
  "booking_rules",
]);

function extractPassthroughSettings(
  settings: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(settings)) {
    if (!SETTINGS_COLUMN_KEYS.has(key)) out[key] = value;
  }
  return out;
}

// Bornes d'une règle : des dates (saisons, séjours), ou une plage de durées
// pour le prix selon la durée, qui vaut toute l'année.
function ruleBounds(type: PricingType | undefined, period: RentalPeriod) {
  if (type === "duration_tier") {
    return {
      valid_from: null, valid_to: null, min_days: period.min_days ?? null, max_days: period.max_days ?? null,
    };
  }
  return {
    valid_from: period.start_date || null, valid_to: period.end_date || null, min_days: null, max_days: null,
  };
}

function buildPricingAttributes(pricing?: RentalPricing | Partial<RentalPricing>) {
  if (!pricing?.type || !pricing.periods?.length) {
    return [];
  }
  const priceType = uiPricingTypeToApi[pricing.type];
  return pricing.periods.map((p) => ({
    price_type: priceType,
    amount_cents: Math.round((p.price_per_unit ?? 0) * 100),
    ...ruleBounds(pricing.type, p),
    priority: 0,
  }));
}

export function adaptProductCreatePayload(
  payload: CreateProductPayload,
  ctx: ProductCreateContext,
): CatalogItemCreatePayload {
  const settings = (payload.settings ?? {}) as Record<string, unknown>;

  return {
    item: {
      name: payload.title,
      description: payload.description,
      // `status` est le tri-state autoritaire côté backend
      // (draft|published|archived). Le booléen `active` est dérivé
      // automatiquement via un `before_save` sur le modèle, on n'a donc
      // pas besoin de le synchroniser ici.
      status: payload.status,
      mode: inferModeFromUi(payload),
      kind: categoryIdToKind(payload.category_id),
      stock_strategy: inferStockStrategy(payload),
      // Hébergement : pas de stock-quantité (la dispo est portée par
      // le calendrier + les séjours forfaitaires).
      // Voyage : pas de stock d'article, les places sont sur les départs.
      stock_quantity: isHebergement(payload) || isVoyage(payload)
        ? null
        : (typeof settings.stock_quantity === "number" ? settings.stock_quantity : null),
      // Voyage : validation par l'élu par défaut (contrôle de la tranche
      // déclarée), sauf choix explicite d'une réservation directe.
      purchase_workflow: isVoyage(payload)
        ? (settings.purchase_workflow === "direct" ? "direct" : "approval")
        : (settings.purchase_workflow === "approval" ? "approval" : "direct"),
      // Only emit check_in_day when start_days expresses a real constraint
      // (a single allowed weekday). The wizard's default is [1..7] meaning
      // "any day" - passing start_days[0] in that case would wrongly pin
      // the item to Monday on the backend.
      // Hébergement forfaitaire (`period_total`) : jour de récupération /
      // restitution portés par chaque séjour → jamais au niveau item.
      check_in_day: (() => {
        if (isHebergement(payload) && payload.pricing?.type !== "per_night") return null;
        const days = payload.availability?.start_days ?? [];
        if (days.length === 1) return days[0];
        if (typeof settings.check_in_day === "number") return settings.check_in_day;
        return null;
      })(),
      check_out_day:
        isHebergement(payload) && payload.pricing?.type !== "per_night"
          ? null
          : (typeof settings.check_out_day === "number" ? settings.check_out_day : null),
      // Hébergement forfaitaire : durée portée par chaque séjour →
      // on n'envoie pas min/max global. Hébergement saisonnier
      // (`per_night`) : durée min/max + tranche au niveau item.
      min_duration_days: isHebergement(payload)
        ? (payload.pricing?.type === "per_night"
          ? (typeof settings.min_duration_days === "number" ? settings.min_duration_days : null)
          : null)
        : (payload.availability?.min_duration ?? null),
      max_duration_days: isHebergement(payload)
        ? (payload.pricing?.type === "per_night"
          ? (typeof settings.max_duration_days === "number" ? settings.max_duration_days : null)
          : null)
        : (typeof settings.max_duration_days === "number" ? settings.max_duration_days : null),
      duration_step_days: isHebergement(payload) && payload.pricing?.type === "per_night"
        ? (typeof settings.duration_step_days === "number" ? settings.duration_step_days : null)
        : null,
      // Iso `adaptProductUpdatePayload` : la caution et la cascade CB
      // sont first-class côté top-level désormais. Fallback `settings`
      // gardé pour les callers historiques.
      deposit_amount_cents:
        typeof payload.deposit_amount_cents === "number"
          ? payload.deposit_amount_cents
          : typeof settings.deposit_amount_cents === "number"
            ? settings.deposit_amount_cents
            : 0,
      cb_payment_enabled:
        typeof payload.cb_payment_enabled === "boolean" || payload.cb_payment_enabled === null
          ? payload.cb_payment_enabled
          : typeof settings.cb_payment_enabled === "boolean" || settings.cb_payment_enabled === null
            ? settings.cb_payment_enabled
            : null,
      // Cascade gestion-du-stock : top-level prime, fallback `settings`
      // (où le wizard la stocke). `null` = hérite de la collection.
      // Voyage : rien à remettre ni à reprendre, gestion du stock coupée.
      stock_management_enabled: isVoyage(payload)
        ? false
        : typeof payload.stock_management_enabled === "boolean" || payload.stock_management_enabled === null
          ? payload.stock_management_enabled
          : typeof settings.stock_management_enabled === "boolean" || settings.stock_management_enabled === null
            ? settings.stock_management_enabled
            : null,
      // Cascade « le panier bloque le créneau » : top-level prime, fallback
      // `settings`. `null` = hérite de la collection.
      cart_blocks_slot:
        typeof payload.cart_blocks_slot === "boolean" || payload.cart_blocks_slot === null
          ? payload.cart_blocks_slot
          : typeof settings.cart_blocks_slot === "boolean" || settings.cart_blocks_slot === null
            ? settings.cart_blocks_slot
            : null,
      // Méthodes de remise + élus destinataires des notifications.
      // Hoist depuis `settings` (où le wizard les stocke) vers les
      // colonnes dédiées côté API.
      delivery_methods: Array.isArray(settings.delivery_methods)
        ? (settings.delivery_methods as string[])
        : [],
      notification_recipient_ids: Array.isArray(settings.notification_recipient_ids)
        ? (settings.notification_recipient_ids as Array<number | string>)
          .map((id) => Number(id))
          .filter((id) => Number.isFinite(id))
        : [],
      // Champs descriptifs catégorie-spécifiques (capacity, bedrooms,
      // surface, location, amenities, pets_allowed, condition, brand, …)
      // → jsonb `settings`. Sans ce passthrough ils étaient perdus à la
      // création (invisibles côté benef + champs vides à l'édition mini).
      settings: extractPassthroughSettings(settings),
      ...(hasBookingRules(normalizeBookingRules(settings.booking_rules))
        ? { booking_rules: toApiBookingRules(settings.booking_rules) }
        : {}),
      catalog_collection_id: ctx.catalogCollectionId,
      category_ids: payload.category_ids ?? [],
      complement_item_ids: payload.complement_item_ids ?? [],
      image_signed_ids: payload.image_signed_ids ?? [],
      pricing_rules_attributes: buildPricingAttributes(payload.pricing),
      // Le wizard passe directement les drafts du builder ; on ne traite
      // ni `id` ni `_destroy` ici puisqu'on est en création.
      form_fields_attributes: (payload.form_fields ?? [])
        .filter((f) => f.label.trim() !== "")
        .map((f, index) => ({
          name: f.name || `field_${index + 1}`,
          label: f.label,
          field_type: f.field_type,
          required: f.required,
          rank: index,
          options: f.options ?? {},
        })),
      // Départs : propres au voyage. L'assistant refuse un départ sans
      // dates, le filtre ne fait que parer un brouillon vide.
      slots_attributes: (isVoyage(payload) ? payload.slots ?? [] : [])
        .filter((s) => s.start_date && s.end_date)
        .map((s) => ({
          name: s.name,
          start_date: s.start_date,
          end_date: s.end_date,
          capacity: s.capacity,
          active: s.active,
        })),
      // Variantes saisies via le wizard. Mêmes règles que côté update :
      // si un `price` est renseigné, on crée 1 pricing_rule polymorphe
      // sur le variant (price_type per_unit/flat selon UI), sinon le
      // variant cascade sur les pricing_rules de l'item.
      // `calendar` : pas de variantes (item unique) - strip défensif au
      // cas où un payload stale traînerait après bascule quantity→calendar.
      // Hébergement : exception, les variantes sont des tarifs du même
      // logement (cf. `isTarifCategory`), une règle par saison.
      variants_attributes: (inferStockStrategy(payload) === "calendar" && !isHebergement(payload) ? [] : (payload.variants ?? []))
        .filter((v) => v.name.trim() !== "")
        .map((v) => {
          const base: {
            name: string
            description?: string | null
            active?: boolean
            sku?: string | null
            stock_quantity?: number | null
            pricing_rules_attributes?: Array<{
              price_type: CatalogPriceType
              amount_cents?: number
              valid_from?: string | null
              valid_to?: string | null
              priority?: number
            }>
          } = {
            name: v.name,
            description: v.description,
            active: v.active,
            sku: v.sku,
            stock_quantity: v.stock_quantity,
          };
          if (isHebergement(payload)) {
            base.stock_quantity = null;
            const tarifRules = buildTarifPricingAttributes(v, payload.pricing);
            if (tarifRules.length > 0) base.pricing_rules_attributes = tarifRules;
            return base;
          }
          const hasPrice = typeof v.price === "number" && v.price > 0 && payload.pricing?.type !== "duration_tier";
          if (hasPrice) {
            // Le price_type du variant hérite de l'item (SettingsPanel
            // est la source de vérité). Fallback variant puis "fixed".
            const effectivePriceType = payload.pricing?.type ?? v.price_type ?? "fixed";
            base.pricing_rules_attributes = [{
              price_type: uiPricingTypeToApi[effectivePriceType],
              amount_cents: Math.round(v.price! * 100),
              valid_from: null,
              valid_to: null,
              priority: 0,
            }];
          }
          return base;
        }),
    },
  };
}

type ProductUpdateContext = {
  /**
   * The pricing rules that existed on the server before the user opened the
   * edit form. Required so the adapter can emit `_destroy: true` markers for
   * any rule the user removed in the UI.
   */
  originalPricing?: RentalPricing
  /**
   * Same idea for form_fields - needed so the adapter can mark deletions
   * as `_destroy: true` instead of silently leaving them in the DB.
   */
  originalFormFields?: RentalFormField[]
  /** Same again for slots. */
  originalSlots?: RentalSlot[]
  /** Same again for variants : nécessaire pour émettre `_destroy: true`
   * sur les variantes retirées + cibler les `pricing_rules` existantes. */
  originalVariants?: RentalVariant[]
};

export function adaptProductUpdatePayload(
  payload: UpdateProductPayload,
  ctx: ProductUpdateContext = {},
): CatalogItemUpdatePayload {
  const item: CatalogItemUpdatePayload["item"] = {};
  const settings = (payload.settings ?? {}) as Record<string, unknown>;

  if (payload.title !== undefined) item.name = payload.title;
  if (payload.description !== undefined) item.description = payload.description;
  if (payload.status !== undefined) item.status = payload.status;
  // `image_signed_ids` est un array : on l'envoie toujours quand le caller
  // l'a explicitement fourni (y compris vide pour purger toutes les
  // photos). Le backend détache puis re-attache dans l'ordre reçu.
  if (payload.image_signed_ids !== undefined) item.image_signed_ids = payload.image_signed_ids;
  if (payload.category_ids !== undefined) item.category_ids = payload.category_ids;
  if (payload.complement_item_ids !== undefined) item.complement_item_ids = payload.complement_item_ids;
  if (payload.category_id !== undefined) {
    item.kind = categoryIdToKind(payload.category_id);
  }

  // `mode` est dérivé automatiquement du kind - l'UI n'expose plus
  // le Select : hébergement = `rental` (location payante), matériel =
  // `loan` (prêt, éventuellement avec un tarif symbolique). Le mode
  // n'a pas de sémantique métier au-delà de cette taxonomie (cf.
  // `Item.mode` côté API, utilisé presque uniquement pour les scopes
  // `rentals`/`sales`/`loans` et la bypass return sur `sale?`).
  const kindForMode = item.kind ?? (payload.category_id ? categoryIdToKind(payload.category_id) : undefined);
  if (kindForMode === "hebergement") {
    item.mode = "rental";
  }
  else if (kindForMode === "materiel" || kindForMode === "livre") {
    item.mode = "loan";
  }
  else if (kindForMode === "voyage") {
    item.mode = "sale";
  }
  else if (settings.mode === "rental" || settings.mode === "sale" || settings.mode === "loan") {
    // Fallback rétro-compat pour les kinds désactivés (vehicule/salle/
    // emplacement) qui pourraient encore traîner sur des items legacy.
    item.mode = settings.mode;
  }
  // Hébergement : on impose calendar-based + pas de stock-quantité.
  // En mode saisonnier (`per_night`), min/max_duration_days et
  // duration_step_days sont au contraire portés par l'item - on les
  // laisse passer. En mode forfaitaire, ils n'ont pas de sens
  // (durée portée par chaque séjour) → on les nullifie.
  const hebergement = isHebergement(payload);
  // La page d'édition n'envoie `pricing` que si l'élu y a touché : à
  // défaut, c'est le type d'origine qui dit saisonnier ou forfait. Sans
  // ce repli, enregistrer un hébergement saisonnier sans toucher à ses
  // tarifs le traitait en forfait et effaçait jour de restitution et
  // durées.
  const hebergementSaisonnier = hebergement
    && (payload.pricing?.type ?? ctx.originalPricing?.type) === "per_night";
  if (hebergement) {
    item.stock_strategy = "calendar";
    item.stock_quantity = null;
    if (!hebergementSaisonnier) {
      item.min_duration_days = null;
      item.max_duration_days = null;
      item.duration_step_days = null;
    }
  }
  else if (isVoyage(payload)) {
    item.stock_strategy = "quantity";
    item.stock_quantity = null;
  }
  else {
    if (settings.stock_strategy === "quantity" || settings.stock_strategy === "calendar") {
      item.stock_strategy = settings.stock_strategy;
    }
    if (typeof settings.stock_quantity === "number" || settings.stock_quantity === null) {
      item.stock_quantity = settings.stock_quantity;
    }
    if (typeof settings.max_duration_days === "number" || settings.max_duration_days === null) {
      item.max_duration_days = settings.max_duration_days;
    }
  }
  if (hebergementSaisonnier) {
    if (typeof settings.min_duration_days === "number" || settings.min_duration_days === null) {
      item.min_duration_days = settings.min_duration_days;
    }
    if (typeof settings.max_duration_days === "number" || settings.max_duration_days === null) {
      item.max_duration_days = settings.max_duration_days;
    }
    if (typeof settings.duration_step_days === "number" || settings.duration_step_days === null) {
      item.duration_step_days = settings.duration_step_days;
    }
  }
  if (settings.purchase_workflow === "direct" || settings.purchase_workflow === "approval") {
    item.purchase_workflow = settings.purchase_workflow;
  }
  // Hébergement forfaitaire (`period_total`) : le jour de récupération /
  // restitution est porté par chaque séjour, pas par l'item. On purge les
  // valeurs « par nuit » résiduelles (sinon le backend rejette
  // « start_date doit tomber le jour X »). Saisonnier / non-hébergement :
  // on honore le réglage de l'élu.
  if (hebergement && !hebergementSaisonnier) {
    item.check_in_day = null;
    item.check_out_day = null;
  }
  else {
    if (typeof settings.check_in_day === "number" || settings.check_in_day === null) {
      item.check_in_day = settings.check_in_day;
    }
    if (typeof settings.check_out_day === "number" || settings.check_out_day === null) {
      item.check_out_day = settings.check_out_day;
    }
  }
  // Caution (cents) + cascade CB : désormais first-class sur le payload
  // (UpdateProductPayload top-level). On garde un fallback `settings`
  // pour les callers historiques qui pourraient encore l'envoyer par là.
  if (typeof payload.deposit_amount_cents === "number") {
    item.deposit_amount_cents = payload.deposit_amount_cents;
  }
  else if (typeof settings.deposit_amount_cents === "number") {
    item.deposit_amount_cents = settings.deposit_amount_cents;
  }
  if (typeof payload.cb_payment_enabled === "boolean" || payload.cb_payment_enabled === null) {
    item.cb_payment_enabled = payload.cb_payment_enabled;
  }
  else if (typeof settings.cb_payment_enabled === "boolean" || settings.cb_payment_enabled === null) {
    item.cb_payment_enabled = settings.cb_payment_enabled;
  }
  // Voyage : déjà forcé à `false` plus haut, la saisie n'y change rien.
  if (isVoyage(payload)) {
    item.stock_management_enabled = false;
  }
  else if (typeof payload.stock_management_enabled === "boolean" || payload.stock_management_enabled === null) {
    item.stock_management_enabled = payload.stock_management_enabled;
  }
  else if (typeof settings.stock_management_enabled === "boolean" || settings.stock_management_enabled === null) {
    item.stock_management_enabled = settings.stock_management_enabled;
  }
  if (typeof payload.cart_blocks_slot === "boolean" || payload.cart_blocks_slot === null) {
    item.cart_blocks_slot = payload.cart_blocks_slot;
  }
  else if (typeof settings.cart_blocks_slot === "boolean" || settings.cart_blocks_slot === null) {
    item.cart_blocks_slot = settings.cart_blocks_slot;
  }

  if (!hebergement && payload.availability?.min_duration !== undefined) {
    item.min_duration_days = payload.availability.min_duration;
  }
  if (payload.availability?.start_days?.length) {
    item.check_in_day = payload.availability.start_days[0];
  }

  // Méthodes de livraison + élus destinataires : on les hoist depuis
  // `settings` vers le payload top-level pour les colonnes dédiées
  // côté API (`delivery_methods` jsonb array, `notification_recipient_ids`
  // integer array). Si le caller envoie `[]`, on transmet quand même
  // (l'élu peut vouloir vider la liste).
  if (Array.isArray(settings.delivery_methods)) {
    item.delivery_methods = settings.delivery_methods as string[];
  }
  if (Array.isArray(settings.notification_recipient_ids)) {
    item.notification_recipient_ids = (settings.notification_recipient_ids as Array<number | string>)
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id));
  }

  // Règles de réservation : toujours renvoyées quand le caller les porte,
  // vides comprises (l'élu peut les retirer).
  if (settings.booking_rules !== undefined) {
    item.booking_rules = toApiBookingRules(settings.booking_rules);
  }

  // Passthrough `settings` jsonb : champs descriptifs catégorie-spécifiques
  // (condition, brand, amenities, capacity, …) — cf. extractPassthroughSettings.
  const passthroughSettings = extractPassthroughSettings(settings);
  if (Object.keys(passthroughSettings).length > 0) {
    item.settings = passthroughSettings;
  }

  if (payload.pricing) {
    item.pricing_rules_attributes = buildPricingUpdateAttributes(
      payload.pricing,
      ctx.originalPricing,
    );
  }

  if (payload.form_fields) {
    item.form_fields_attributes = buildFormFieldsUpdateAttributes(
      payload.form_fields,
      ctx.originalFormFields,
    );
  }

  if (payload.slots) {
    item.slots_attributes = buildSlotsUpdateAttributes(
      payload.slots,
      ctx.originalSlots,
    );
  }

  // Hébergement : les règles d'un tarif suivent les saisons de l'article.
  // Modifier les saisons réécrit donc aussi les tarifs, même si l'élu n'a
  // pas touché à la section Tarifs.
  if (isTarifCategory(payload.category_id) && (payload.variants || payload.pricing)) {
    const pricing = { ...ctx.originalPricing, ...payload.pricing } as Partial<RentalPricing>;
    const variants = payload.variants ?? ctx.originalVariants ?? [];
    if (variants.length > 0 || (ctx.originalVariants?.length ?? 0) > 0) {
      item.variants_attributes = buildVariantsUpdateAttributes(
        variants,
        ctx.originalVariants,
        pricing.type,
        pricing,
      );
    }
  }
  else if (payload.variants) {
    // Le `price_type` de l'item est la source de vérité (cf.
    // SettingsPanel) - les variants en héritent au save pour que leur
    // pricing_rule porte le même price_type que l'item. Évite qu'un
    // variant créé en `per_day` reste en `per_day` après bascule de
    // l'item en `fixed`.
    const itemPriceType = payload.pricing?.type;
    // `calendar` = item unique → on force la liste de variantes à vide
    // pour que `buildVariantsUpdateAttributes` émette `_destroy: true`
    // sur toutes les variantes pré-existantes en DB (cas bascule
    // quantity→calendar sur un item legacy).
    const variantsForSave = item.stock_strategy === "calendar" ? [] : payload.variants;
    item.variants_attributes = buildVariantsUpdateAttributes(
      variantsForSave,
      ctx.originalVariants,
      itemPriceType,
    );
  }

  return { item };
}

/**
 * Build `variants_attributes` pour un update Item, avec
 * `pricing_rules_attributes` imbriqué. Chaque variante porte au plus
 * une règle de tarif unitaire (cas billetterie : VIP / Fosse), gérée
 * via 3 sous-cas : si le variant arrive avec une `pricing_rule_id`
 * existante et un `price` défini → update de la règle ; si le variant
 * n'a pas de `pricing_rule_id` mais un `price` → create ; si `price`
 * passe à `null`/`undefined` alors qu'une règle existait → destroy
 * pour faire cascade au tarif item parent.
 */
function buildVariantsUpdateAttributes(
  variants: RentalVariant[],
  original?: RentalVariant[],
  itemPriceType?: PricingType,
  /** Hébergement : tarifs par saison (cf. `buildTarifPricingAttributes`). */
  tarifPricing?: Partial<RentalPricing>,
): Array<{
  id?: string
  name?: string
  description?: string | null
  active?: boolean
  sku?: string | null
  stock_quantity?: number | null
  _destroy?: boolean
  pricing_rules_attributes?: Array<{
    id?: string
    price_type: CatalogPriceType
    amount_cents?: number
    valid_from?: string | null
    valid_to?: string | null
    priority?: number
    _destroy?: boolean
  }>
}> {
  const seenIds = new Set<string>();
  const originalById = new Map(
    (original ?? [])
      .filter((v): v is RentalVariant & { id: string } => typeof v.id === "string")
      .map((v) => [v.id, v]),
  );

  const upserts = variants
    .filter((v) => v.name.trim() !== "")
    .map((v) => {
      const isExisting = typeof v.id === "string" && v.id.startsWith("CVA_");
      if (isExisting) seenIds.add(v.id!);
      const originalVariant = isExisting ? originalById.get(v.id!) : undefined;

      const pricingAttributes = tarifPricing
        ? buildTarifPricingAttributes(v, tarifPricing)
        : buildVariantPricingAttributes(v, originalVariant, itemPriceType);

      return {
        ...(isExisting ? { id: v.id } : {}),
        name: v.name,
        description: v.description,
        active: v.active,
        sku: v.sku,
        stock_quantity: tarifPricing ? null : v.stock_quantity,
        ...(pricingAttributes.length > 0
          ? { pricing_rules_attributes: pricingAttributes }
          : {}),
      };
    });

  const destroys = (original ?? [])
    .filter((v): v is RentalVariant & { id: string } =>
      typeof v.id === "string" && v.id.startsWith("CVA_") && !seenIds.has(v.id))
    .map((v) => ({ id: v.id, _destroy: true }));

  return [...upserts, ...destroys];
}

/** Diff la pricing_rule d'un variant entre l'état UI et l'état original
 * pour décider create/update/destroy. */
function buildVariantPricingAttributes(
  variant: RentalVariant,
  original?: RentalVariant,
  itemPriceType?: PricingType,
): Array<{
  id?: string
  price_type: CatalogPriceType
  amount_cents?: number
  valid_from?: string | null
  valid_to?: string | null
  priority?: number
  _destroy?: boolean
}> {
  // Prix selon la durée : les variantes suivent la grille de l'article
  // (une règle sans plage serait refusée) ; on retire leur prix propre.
  const followsDurationGrid = (itemPriceType ?? variant.price_type) === "duration_tier";
  const hasPrice = !followsDurationGrid && typeof variant.price === "number" && variant.price > 0;
  const existingRuleId = original?.pricing_rule_id;
  // Le `price_type` de l'item gagne sur celui du variant - l'élu choisit
  // le Mode de facturation au niveau item dans SettingsPanel et toutes
  // les variantes en héritent. Fallback sur celui du variant pour les
  // appels legacy qui ne passent pas `itemPriceType`.
  const effectivePriceType = itemPriceType ?? variant.price_type ?? "fixed";

  if (!hasPrice && existingRuleId) {
    return [{
      id: existingRuleId,
      price_type: uiPricingTypeToApi[original?.price_type ?? effectivePriceType],
      _destroy: true,
    }];
  }

  if (!hasPrice) {
    return [];
  }

  const priceType = uiPricingTypeToApi[effectivePriceType];
  const amountCents = Math.round(variant.price! * 100);
  return [{
    ...(existingRuleId ? { id: existingRuleId } : {}),
    price_type: priceType,
    amount_cents: amountCents,
    valid_from: null,
    valid_to: null,
    priority: 0,
  }];
}

/**
 * Règles d'un tarif d'hébergement : une par saison de l'article, mêmes
 * bornes et même `price_type`, au prix du tarif (vide = prix de base de
 * la saison, pour que le tarif couvre toujours toute l'année). Les règles
 * d'une saison retirée, ou qui ne correspondaient à aucune saison au
 * chargement, sont supprimées. L'ordre n'y change rien côté API : le
 * contrôle de chevauchement compare chaque règle à la base avant toute
 * écriture, comme pour les saisons de l'article.
 */
function buildTarifPricingAttributes(
  variant: RentalVariant,
  pricing?: Partial<RentalPricing>,
): Array<{
  id?: string
  price_type: CatalogPriceType
  amount_cents?: number
  valid_from?: string | null
  valid_to?: string | null
  priority?: number
  _destroy?: boolean
}> {
  if (!pricing?.type) return [];
  const priceType = uiPricingTypeToApi[pricing.type];
  const periods = pricing.periods ?? [];
  const seasonPrices = variant.season_prices ?? [];
  const byPeriod = new Map(seasonPrices.map((sp) => [sp.period_id, sp]));
  const periodIds = new Set(periods.map((p) => p.id));

  const destroyIds = [
    ...seasonPrices
      .filter((sp) => sp.rule_id && !periodIds.has(sp.period_id))
      .map((sp) => sp.rule_id!),
    ...(variant.stale_rule_ids ?? []),
  ];
  const destroys = destroyIds.map((id) => ({ id, price_type: priceType, _destroy: true }));

  const upserts = periods.map((period) => {
    const seasonPrice = byPeriod.get(period.id);
    const price = typeof seasonPrice?.price === "number"
      ? seasonPrice.price
      : (period.price_per_unit ?? 0);
    return {
      ...(seasonPrice?.rule_id ? { id: seasonPrice.rule_id } : {}),
      price_type: priceType,
      amount_cents: Math.round(price * 100),
      valid_from: period.start_date || null,
      valid_to: period.end_date || null,
      priority: 0,
    };
  });

  return [...destroys, ...upserts];
}

/**
 * Build `slots_attributes` for an Item update. Same id-resolution
 * pattern as form_fields: existing rows keep their `CSL_…` id, new
 * drafts ship without id, and removals are sent as `_destroy: true`.
 */
function buildSlotsUpdateAttributes(
  slots: RentalSlot[],
  original?: RentalSlot[],
): Array<{
  id?: string
  name?: string | null
  start_date?: string
  end_date?: string
  capacity?: number | null
  active?: boolean
  _destroy?: boolean
}> {
  const seenIds = new Set<string>();
  const upserts = slots
    .filter((s) => s.start_date && s.end_date) // skip empty drafts
    .map((s) => {
      const isExisting = typeof s.id === "string" && s.id.startsWith("CSL_");
      if (isExisting) seenIds.add(s.id);
      return {
        ...(isExisting ? { id: s.id } : {}),
        name: s.name,
        start_date: s.start_date,
        end_date: s.end_date,
        capacity: s.capacity,
        active: s.active,
      };
    });

  const destroys = (original ?? [])
    .filter((s) => s.id.startsWith("CSL_") && !seenIds.has(s.id))
    .map((s) => ({ id: s.id, _destroy: true }));

  return [...upserts, ...destroys];
}

/**
 * Build `form_fields_attributes` for an Item update.
 *
 * Fields with a `CFF_…` id come from the API → emit as updates.
 * Fields with a temp id (`tmp-…`) → emit without id so Rails creates them.
 * Original fields missing from the new list → emit `_destroy: true`.
 */
function buildFormFieldsUpdateAttributes(
  fields: RentalFormField[],
  original?: RentalFormField[],
): Array<{
  id?: string
  name?: string
  label?: string
  field_type?: CatalogFormFieldType
  required?: boolean
  rank?: number
  options?: { choices?: string[] } & Record<string, unknown>
  _destroy?: boolean
}> {
  const seenIds = new Set<string>();
  const upserts = fields.map((f, index) => {
    const isExisting = typeof f.id === "string" && f.id.startsWith("CFF_");
    if (isExisting) seenIds.add(f.id);
    // Le `name` est auto-dérivé du label côté UI - fallback sur `field_<rank>`
    // si la dérivation a produit une chaîne vide (libellé exotique).
    const safeName = f.name || `field_${index + 1}`;
    return {
      ...(isExisting ? { id: f.id } : {}),
      name: safeName,
      label: f.label,
      field_type: f.field_type,
      required: f.required,
      rank: index, // rank from list order - let the user reorder later if needed
      options: f.options ?? {},
    };
  });

  const destroys = (original ?? [])
    .filter((f) => f.id.startsWith("CFF_") && !seenIds.has(f.id))
    .map((f) => ({ id: f.id, _destroy: true }));

  return [...upserts, ...destroys];
}

/**
 * Build `pricing_rules_attributes` for an update.
 *
 * Rules tagged with a `CPR_` prefix id come from the API and become updates;
 * the rest (UUIDs from `crypto.randomUUID()`) are new and become creates.
 * Any rule that was on the server but is missing from the new periods list
 * gets a `_destroy: true` marker so Rails' `accepts_nested_attributes_for`
 * deletes it.
 */
function buildPricingUpdateAttributes(
  pricing: RentalPricing,
  original?: RentalPricing,
): Array<{
  id?: string
  price_type: CatalogPriceType
  amount_cents?: number
  valid_from?: string | null
  valid_to?: string | null
  priority?: number
  _destroy?: boolean
}> {
  const priceType = uiPricingTypeToApi[pricing.type];
  const seenIds = new Set<string>();

  const upserts = pricing.periods.map((p) => {
    const isExisting = typeof p.id === "string" && p.id.startsWith("CPR_");
    if (isExisting) seenIds.add(p.id);
    return {
      ...(isExisting ? { id: p.id } : {}),
      price_type: priceType,
      amount_cents: Math.round((p.price_per_unit ?? 0) * 100),
      ...ruleBounds(pricing.type, p),
      priority: 0,
    };
  });

  const destroys = (original?.periods ?? [])
    .filter((p) => p.id.startsWith("CPR_") && !seenIds.has(p.id))
    .map((p) => ({
      id: p.id,
      price_type: priceType,
      _destroy: true,
    }));

  return [...upserts, ...destroys];
}
